/**
 * TextToSpeech Module
 *
 * A comprehensive Text-to-Speech module that provides:
 * - Cross-platform voice detection and management
 * - Automatic language detection
 * - Event-driven architecture
 * - Chrome compatibility with text chunking
 * - Voice preference configuration
 * - Robust error handling and cleanup
 */

class TextToSpeech extends EventTarget {
  constructor(options = {}) {
    super()

    this.state = {
      isPlaying: false,
      stopRequested: false,
      currentUtterance: null,
      currentChunks: [],
      currentChunkIndex: 0,
    }

    this.voices = {
      available: [],
      loaded: false,
      preferences: {
        'en': {
          preferredVoices: ['Alex', 'Samantha', 'Microsoft David Desktop', 'Google US English'],
          fallbackLang: 'en-US'
        },
        'zh-CN': {
          preferredVoices: ['Ting-Ting', 'Microsoft Huihui Desktop', 'Google 普通话（中国大陆）'],
          fallbackLang: 'zh-CN'
        },
        'zh-TW': {
          preferredVoices: ['Mei-Jia', 'Microsoft Hanhan Desktop', 'Google 國語（臺灣）'],
          fallbackLang: 'zh-TW'
        },
        'ja': {
          preferredVoices: ['Kyoko', 'Microsoft Haruka Desktop', 'Google 日本語'],
          fallbackLang: 'ja-JP'
        },
        'ko': {
          preferredVoices: ['Yuna', 'Microsoft Heami Desktop', 'Google 한국어'],
          fallbackLang: 'ko-KR'
        }
      }
    }

    this.options = {
      rate: 1.0,
      pitch: 1.0,
      volume: 1.0,
      autoDetectLanguage: true,
      chromeChunkSize: 200,
      debug: false,
      ...options
    }

    this.handlePageUnload = this.handlePageUnload.bind(this)

    this.initialize()
  }

  // Initialization Methods
  // ----------------------------------------------------------------

  initialize() {
    if (!this.isSupported()) {
      console.warn('[TTS] Speech synthesis not supported in this browser')
      this.dispatchEvent(new CustomEvent('tts:error', {
        detail: { error: 'Speech synthesis not supported' }
      }))
      return
    }

    this.initializeVoices()
    this.setupPageUnloadCleanup()

    if (this.options.debug) {
      console.log('[TTS] TextToSpeech module initialized')
    }
  }

  isSupported() {
    return 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window
  }

  initializeVoices() {
    // Try to get voices immediately (Firefox/Safari)
    this.loadVoices()

    // Also listen for voiceschanged event (Chrome)
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = () => {
        this.loadVoices()
      }
    }
  }

  loadVoices() {
    const voices = window.speechSynthesis.getVoices()
    if (voices.length > 0) {
      this.voices.available = voices
      this.voices.loaded = true

      if (this.options.debug) {
        console.log('[TTS] Loaded voices:', voices.map(v => `${v.name} (${v.lang})`))
      }

      this.dispatchEvent(new CustomEvent('tts:voices-loaded', {
        detail: { voices: voices, count: voices.length }
      }))
    }
  }

  setupPageUnloadCleanup() {
    window.addEventListener('beforeunload', this.handlePageUnload)
  }

  handlePageUnload() {
    this.stop()
  }

  destroy() {
    this.stop()
    window.removeEventListener('beforeunload', this.handlePageUnload)

    if (window.speechSynthesis.onvoiceschanged) {
      window.speechSynthesis.onvoiceschanged = null
    }
  }

  // Public API Methods
  // ----------------------------------------------------------------

  /**
   * Play text-to-speech
   * @param {string} text - Text to speak
   * @param {string} language - Language code (optional, auto-detected if not provided)
   * @param {Object} options - Override options for this playback
   */
  play(text, language = null, options = {}) {
    if (!text || !text.trim()) {
      console.warn('[TTS] No text provided for TTS playback')
      return false
    }

    // Stop any existing playback
    this.stop()

    // Reset state for new playback
    this.state.stopRequested = false
    this.state.currentChunks = []
    this.state.currentChunkIndex = 0

    // Auto-detect language if not provided
    const detectedLanguage = language || (this.options.autoDetectLanguage ? this.detectLanguage(text) : 'en')

    this.dispatchEvent(new CustomEvent('tts:language-detected', {
      detail: { language: detectedLanguage, text: text }
    }))

    // Find best voice for the detected language
    const selectedVoice = this.findBestVoiceForLanguage(detectedLanguage)
    if (!selectedVoice) {
      console.error(`[TTS] No voice available for language: ${detectedLanguage}`)
      this.dispatchEvent(new CustomEvent('tts:error', {
        detail: { error: `No voice available for language: ${detectedLanguage}` }
      }))
      return false
    }

    this.dispatchEvent(new CustomEvent('tts:voice-selected', {
      detail: { voice: selectedVoice, language: detectedLanguage }
    }))

    // Check if we need to split text for Chrome
    const isChrome = /Chrome/.test(navigator.userAgent)
    const textChunks = isChrome ? this.splitTextForChrome(text) : [text]

    this.state.currentChunks = textChunks
    this.playChunks(textChunks, selectedVoice, { ...this.options, ...options })

    return true
  }

  /**
   * Stop current TTS playback
   */
  stop() {
    if (this.state.currentUtterance || this.state.isPlaying) {
      // Set stop flag BEFORE canceling to prevent chunk continuation
      this.state.stopRequested = true

      // Cancel any ongoing speech
      window.speechSynthesis.cancel()

      // Reset state
      this.state.currentUtterance = null
      this.state.isPlaying = false
      this.state.currentChunks = []
      this.state.currentChunkIndex = 0

      this.dispatchEvent(new CustomEvent('tts:stopped', {
        detail: { reason: 'user-requested' }
      }))

      if (this.options.debug) {
        console.log('[TTS] Playback stopped')
      }
    }
  }

  /**
   * Toggle TTS playback (play if stopped, stop if playing)
   * @param {string} text - Text to play (if starting playback)
   * @param {string} language - Language code (optional)
   * @param {Object} options - Playback options
   */
  toggle(text = null, language = null, options = {}) {
    if (this.state.isPlaying) {
      this.stop()
      return 'stopped'
    } else if (text) {
      this.play(text, language, options)
      return 'playing'
    } else {
      console.warn('[TTS] No text provided for toggle play')
      return 'no-text'
    }
  }

  /**
   * Check if TTS is currently playing
   * @returns {boolean}
   */
  isPlaying() {
    return this.state.isPlaying
  }

  /**
   * Set voice preferences for languages
   * @param {Object} preferences - Language to voice preference mapping
   */
  setVoicePreferences(preferences) {
    this.voices.preferences = { ...this.voices.preferences, ...preferences }

    this.dispatchEvent(new CustomEvent('tts:preferences-updated', {
      detail: { preferences: preferences, languages: Object.keys(preferences) }
    }))

    if (this.options.debug) {
      console.log('[TTS] Voice preferences updated for languages:', Object.keys(preferences))
    }
  }

  /**
   * Get available voices for a specific language
   * @param {string} language - Language code
   * @returns {Array} Array of available voices
   */
  getAvailableVoices(language = null) {
    if (!this.voices.loaded) {
      this.loadVoices()
    }

    if (!language) {
      return this.voices.available
    }

    // Normalize language code (handle both 'zh-CN' and 'zh' formats)
    const normalizedLang = language.toLowerCase()
    const langPrefix = normalizedLang.split('-')[0]

    return this.voices.available.filter(voice => {
      const voiceLang = voice.lang.toLowerCase()
      return voiceLang === normalizedLang ||
             voiceLang.startsWith(langPrefix + '-') ||
             voiceLang === langPrefix
    })
  }

  // Core TTS Implementation
  // ----------------------------------------------------------------

  playChunks(chunks, voice, options) {
    this.playChunkAtIndex(chunks, voice, options, 0)
  }

  playChunkAtIndex(chunks, voice, options, chunkIndex) {
    if (chunkIndex >= chunks.length) {
      // All chunks played
      this.state.isPlaying = false
      this.state.currentUtterance = null
      this.state.currentChunks = []
      this.state.currentChunkIndex = 0

      this.dispatchEvent(new CustomEvent('tts:ended', {
        detail: { totalChunks: chunks.length }
      }))

      if (this.options.debug) {
        console.log('[TTS] All chunks completed')
      }
      return
    }

    const chunk = chunks[chunkIndex]
    const utterance = new SpeechSynthesisUtterance(chunk)

    // Set voice and language
    utterance.voice = voice
    utterance.lang = voice.lang

    // Set voice properties
    utterance.rate = options.rate
    utterance.pitch = options.pitch
    utterance.volume = options.volume

    // Event listeners
    utterance.onstart = () => {
      if (chunkIndex === 0) {
        this.state.isPlaying = true
        this.state.currentChunkIndex = chunkIndex

        this.dispatchEvent(new CustomEvent('tts:started', {
          detail: {
            text: chunks.join(''),
            voice: voice,
            language: voice.lang,
            totalChunks: chunks.length
          }
        }))

        if (this.options.debug) {
          console.log('[TTS] Playback started with voice:', voice.name)
        }
      }
    }

    utterance.onend = () => {
      // Check if stop was requested before playing next chunk
      if (this.state.stopRequested) {
        this.dispatchEvent(new CustomEvent('tts:chunk-stopped', {
          detail: {
            chunkIndex: chunkIndex + 1,
            totalChunks: chunks.length,
            reason: 'stop-requested'
          }
        }))
        return
      }

      this.dispatchEvent(new CustomEvent('tts:chunk-ended', {
        detail: {
          chunkIndex: chunkIndex + 1,
          totalChunks: chunks.length
        }
      }))

      // Play next chunk
      this.playChunkAtIndex(chunks, voice, options, chunkIndex + 1)
    }

    utterance.onerror = (event) => {
      this.state.isPlaying = false
      this.state.currentUtterance = null

      this.dispatchEvent(new CustomEvent('tts:error', {
        detail: {
          error: event.error,
          chunkIndex: chunkIndex + 1,
          totalChunks: chunks.length
        }
      }))

      console.error('[TTS] Error:', event.error)
    }

    // Store utterance and start speaking
    this.state.currentUtterance = utterance
    this.state.currentChunkIndex = chunkIndex
    window.speechSynthesis.speak(utterance)
  }

  // Voice Management Methods
  // ----------------------------------------------------------------

  findBestVoiceForLanguage(language) {
    if (this.options.debug) {
      console.debug(`🎤 [TTS Voice Selection] Starting voice selection for language: ${language}`)
    }

    const availableVoices = this.getAvailableVoices(language)

    if (this.options.debug) {
      console.debug(`🎤 [TTS Voice Selection] Found ${availableVoices.length} available voices for ${language}:`,
        availableVoices.map(v => `${v.name} (${v.lang})`))
    }

    if (availableVoices.length === 0) {
      console.warn(`🎤 [TTS Voice Selection] ❌ No voices available for language: ${language}`)
      return null
    }

    const preferences = this.voices.preferences[language] || this.voices.preferences[language.split('-')[0]]

    if (this.options.debug) {
      console.debug(`🎤 [TTS Voice Selection] Voice preferences for ${language}:`, preferences)
    }

    if (!preferences) {
      if (this.options.debug) {
        console.debug(`🎤 [TTS Voice Selection] ⚠️ No preferences set for ${language}, using first available voice: ${availableVoices[0].name}`)
      }
      return availableVoices[0]
    }

    if (this.options.debug) {
      console.debug(`🎤 [TTS Voice Selection] Searching for preferred voices in order:`, preferences.preferredVoices)
    }

    // Try to find preferred voices in order
    for (let i = 0; i < preferences.preferredVoices.length; i++) {
      const preferredName = preferences.preferredVoices[i]

      if (this.options.debug) {
        console.debug(`🎤 [TTS Voice Selection] [${i + 1}/${preferences.preferredVoices.length}] Looking for: "${preferredName}"`)
      }

      const voice = availableVoices.find(v => {
        const nameMatch = v.name.includes(preferredName) || preferredName.includes(v.name)

        if (this.options.debug) {
          console.debug(`🎤 [TTS Voice Selection]   - Checking: "${v.name}" → ${nameMatch ? '✅ MATCH' : '❌ no match'}`)
        }

        return nameMatch
      })

      if (voice) {
        if (this.options.debug) {
          console.debug(`🎤 [TTS Voice Selection] ✅ Found preferred voice: "${voice.name}" (${voice.lang})`)
        }
        return voice
      }
    }

    if (this.options.debug) {
      console.debug(`🎤 [TTS Voice Selection] ⚠️ No preferred voices found, falling back to first available: "${availableVoices[0].name}" (${availableVoices[0].lang})`)
    }

    return availableVoices[0]
  }

  // Utility Methods
  // ----------------------------------------------------------------

  detectLanguage(content) {
    // Simple language detection based on character patterns
    const chinesePattern = /[\u4e00-\u9fff]/
    const japanesePattern = /[\u3040-\u309f\u30a0-\u30ff]/
    const koreanPattern = /[\uac00-\ud7af]/

    if (chinesePattern.test(content)) {
      // Distinguish between Simplified and Traditional Chinese
      const simplifiedIndicators = /[的了是在有个一]/ // Common simplified characters
      const traditionalIndicators = /[個對於來說]/ // Common traditional characters

      if (traditionalIndicators.test(content) && !simplifiedIndicators.test(content)) {
        return 'zh-TW'
      }
      return 'zh-CN'
    }

    if (japanesePattern.test(content)) {
      return 'ja'
    }

    if (koreanPattern.test(content)) {
      return 'ko'
    }

    // Default to English
    return 'en'
  }

  splitTextForChrome(text, maxLength = null) {
    const chunkSize = maxLength || this.options.chromeChunkSize

    // Chrome has character limits for TTS
    if (text.length <= chunkSize) {
      return [text]
    }

    const chunks = []
    let currentChunk = ''
    const sentences = text.split(/[.!?。！？]/g)

    for (const sentence of sentences) {
      if (currentChunk.length + sentence.length <= chunkSize) {
        currentChunk += sentence
      } else {
        if (currentChunk) {
          chunks.push(currentChunk.trim())
        }
        currentChunk = sentence
      }
    }

    if (currentChunk) {
      chunks.push(currentChunk.trim())
    }

    return chunks.filter(chunk => chunk.length > 0)
  }

  // Configuration Methods
  // ----------------------------------------------------------------

  /**
   * Update TTS options
   * @param {Object} newOptions - Options to update
   */
  updateOptions(newOptions) {
    this.options = { ...this.options, ...newOptions }

    this.dispatchEvent(new CustomEvent('tts:options-updated', {
      detail: { options: newOptions }
    }))

    if (this.options.debug) {
      console.log('[TTS] Options updated:', newOptions)
    }
  }

  /**
   * Get current configuration
   * @returns {Object} Current options and state
   */
  getConfiguration() {
    return {
      options: { ...this.options },
      state: { ...this.state },
      voices: {
        available: this.voices.available.length,
        loaded: this.voices.loaded,
        preferences: { ...this.voices.preferences }
      },
      supported: this.isSupported()
    }
  }

  /**
   * Get current playback status
   * @returns {Object} Detailed status information
   */
  getStatus() {
    return {
      isPlaying: this.state.isPlaying,
      stopRequested: this.state.stopRequested,
      currentChunk: this.state.currentChunkIndex + 1,
      totalChunks: this.state.currentChunks.length,
      hasUtterance: !!this.state.currentUtterance,
      voicesLoaded: this.voices.loaded,
      voiceCount: this.voices.available.length
    }
  }
}

export default TextToSpeech